import fromUnixTime from 'date-fns/fromUnixTime';
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { emitter } from 'shared/helpers/mitt';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATVENTAS_RESET,
  CHATVENTAS_SET_USER,
} from '../../constants/appEvents';
import { appendRevisionQuery } from '../../helper/appRevisionGuard';

Cookies.defaults = { sameSite: 'Lax' };

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATVENTAS_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

export const getHeaderExpiry = response =>
  fromUnixTime(response.headers.expiry);

// Antes esta función guardaba el token de auth en una cookie del lado del cliente
// (`wt_d_session_info`). Ahora el backend setea una cookie `__Host-wt_auth` httpOnly
// que el navegador maneja automáticamente. El frontend solo procesa los datos del
// usuario que vienen en el body de la respuesta de login.
//
// Además limpiamos las cookies legacy en cada login exitoso: usuarios que tenían
// sesiones activas antes del deploy de httpOnly traen esas cookies residuales
// (`user.id`, `user.expires_at`, `wt_d_session_info`). Ya no las usamos, pero
// las borramos para evitar confusión en DevTools y prevenir que alguien las
// vuelva a considerar fuente de verdad por error.
export const setAuthCredentials = response => {
  const expiryDate = getHeaderExpiry(response);
  // eslint-disable-next-line no-use-before-define -- declarado más abajo en el módulo
  clearBrowserSessionCookies();
  setUser(response.data.data, expiryDate);
  // Capa 1 anti-cookie-theft: persistir el nonce emitido por el backend. Vive
  // en sessionStorage (per-window) — un atacante que clone cookies a otro
  // navegador/contexto NO obtiene este valor y la primera request al backend
  // responde 401 session_nonce_mismatch. El nonce puede venir vacío cuando
  // el backend cayó al fail-open (Redis no disponible); el interceptor
  // omite el header en ese caso y el rest de defensas (CSRF + binding) operan.
  if (response.data?.session_nonce) {
    SessionStorage.set(
      SESSION_STORAGE_KEYS.SESSION_NONCE,
      response.data.session_nonce
    );
  }
};

// Limpia las cookies residuales del sistema viejo (`wt_d_session_info`, `user.id`, etc.)
// La cookie nueva `__Host-wt_auth` es httpOnly y solo puede ser borrada por el backend
// (el logout endpoint lo hace automáticamente).
export const clearBrowserSessionCookies = () => {
  Cookies.remove('wt_d_session_info');
  Cookies.remove('auth_data');
  Cookies.remove('user');
  Cookies.remove('user.id');
  Cookies.remove('user.expires_at');
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
  // Capa 1 anti-cookie-theft: al logout, borrar el nonce para que una
  // siguiente persona en el mismo browser (kiosco, PC compartida) no pueda
  // aprovecharlo si la cookie quedara residual.
  SessionStorage.remove(SESSION_STORAGE_KEYS.SESSION_NONCE);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('wt-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('wt-idb-names');
};

export const clearCookiesOnLogout = () => {
  emitter.emit(CHATVENTAS_RESET);
  emitter.emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  clearLocalStorageOnLogout();
  clearSessionStorageOnLogout();
  const globalConfig = window.globalConfig || {};
  const logoutRedirectLink = globalConfig.LOGOUT_REDIRECT_LINK || '/';
  // Igual que en login: anexamos `?_v=APP_REVISION` para que la pantalla de
  // login a la que se redirige tras logout se traiga el HTML/bundle más nuevo.
  // Así el siguiente sign-in arranca sobre la última versión desplegada.
  window.location = appendRevisionQuery(logoutRedirectLink);
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
